import { useCallback, useEffect, useState } from "react";
import { flushQueue, queueSize } from "../lib/offlineQueue";
import { API_URL } from "../lib/api";
import { log } from "../lib/logger";
import { WifiOff, RefreshCw, CheckCircle2 } from "lucide-react";

export default function OfflineBanner() {
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [queued, setQueued] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [justSynced, setJustSynced] = useState(false);
  // bump this to trigger a re-fetch of the queue size from the data effect below.
  const [queueTick, setQueueTick] = useState(0);

  const refreshQueue = useCallback(() => setQueueTick((t) => t + 1), []);

  const sync = useCallback(async () => {
    if (!navigator.onLine) return;
    setSyncing(true);
    const token = localStorage.getItem("sohom_token");
    const result = await flushQueue(API_URL, token);
    setSyncing(false);
    refreshQueue();
    if (result.okCount > 0) {
      setJustSynced(true);
      setTimeout(() => setJustSynced(false), 3500);
    }
  }, [refreshQueue]);

  // Read the queue size whenever queueTick changes (mount, interval, after sync).
  // setState happens after `await`, which satisfies react-hooks/set-state-in-effect.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const n = await queueSize();
        if (!cancelled) setQueued(n);
      } catch (err) {
        log.warn("[Sohom] queueSize failed:", err);
      }
    })();
    return () => { cancelled = true; };
  }, [queueTick]);

  // Periodic polling for queue size.
  useEffect(() => {
    const interval = setInterval(refreshQueue, 5000);
    return () => clearInterval(interval);
  }, [refreshQueue]);

  // Online/offline browser events — handlers are event-triggered (rule-allowed).
  useEffect(() => {
    const onOnline = () => { setOnline(true); sync(); };
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [sync]);

  if (online && queued === 0 && !justSynced) return null;

  const banner = (() => {
    if (online && queued > 0) {
      return {
        bg: "bg-sky-50 border-sky-200 text-sky-900",
        icon: <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} />,
        msg: `${queued} change${queued > 1 ? "s" : ""} pending sync`,
      };
    }
    if (online && justSynced) {
      return {
        bg: "bg-emerald-50 border-emerald-200 text-emerald-900",
        icon: <CheckCircle2 className="w-4 h-4" />,
        msg: "All offline changes synced to server.",
      };
    }
    return {
      bg: "bg-amber-50 border-amber-200 text-amber-900",
      icon: <WifiOff className="w-4 h-4" />,
      msg: "You're offline. Lead changes are being queued and will sync automatically.",
    };
  })();

  return (
    <div className={`px-4 py-2 border-b text-xs font-medium flex items-center justify-between ${banner.bg}`} data-testid="offline-banner">
      <div className="flex items-center gap-2">
        {banner.icon}
        <span>{banner.msg}</span>
      </div>
      {online && queued > 0 && (
        <button onClick={sync} disabled={syncing} className="text-xs font-semibold underline disabled:opacity-50" data-testid="sync-now-btn">
          {syncing ? "Syncing…" : "Sync now"}
        </button>
      )}
    </div>
  );
}
